// NPM
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import template from 'lodash/template';
import moment from 'moment';
import { chain, find, map } from 'lodash';
import { zip } from 'rxjs';
import jsCookie from 'js-cookie';
import classnames from 'classnames';
import { withRouter } from 'react-router-dom';

// Glu
import { withStyles } from '@glu/theming';
import { ExclamationTriangleIcon } from '@glu/icons-react';
import { Button, SECONDARY, PRIMARY } from '@glu/buttons-react';

// HOCs
import {
  actionPayments,
  getWireAccounts,
  getFxRates,
  getWirePreferences
} from 'api/payments/service';
import { reverseValidateAction$, updateStaleDatedPayments$ } from 'api/payments/ach.service';
import AppPropTypes from 'proptypes/AppPropTypes';
import { getError } from 'utils/handleError';
import { setPaymentActionMessages } from 'logic/store/payments/actions';
import withLocale from 'components/withLocale/withLocale';
import withRouterConnect from 'components/withRouterConnect/withRouterConnect';

// Constants
import {
  ALERTVARIATIONS,
  PAYMENT_ACTIONS,
  API_ERROR,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  API_JAVA_DATE_TIME_FORMAT,
  WORKSPACES,
  HEADERS,
  ACTIONS,
  PAYMENT_STATUS,
  PAYMENT_CATS,
  PAYMENT_COLS,
  TIME_FRAME,
  DEFAULT_PER_PAGE
} from 'constants.js';

// Components
import Alert from '@glu/alerts-react';
import BasicGrid from 'components/basicGrid/BasicGrid';
import CustomCell from 'components/basicGrid/helpers/CustomCell';
import CustomCellStyles from 'components/basicGrid/helpers/CustomCell.styles';
import Workflow from 'components/workflow/Workflow';
import ChallengeManager from 'components/challengeManager/ChallengeManager';
import Spinner from 'components/spinner/Spinner';
import { getPaymentConfirmationColumns } from './payments.utils';
import PaymentsConfirmationModal from './PaymentsConfirmationModal';
import PaymentsConfirmationAlert from './PaymentsConfirmationAlert';
import PaymentsWireInsufficientFundsAlert from './PaymentsWireInsufficientFundsAlert';

// Services & Utils
// eslint-disable-next-line import/order
import {
  paymentConfirmationColumns,
  paymentConfirmationColumnsAch,
  paymentConfirmationColumnsAchReversal,
  paymentConfirmationColumnsMixed,
  paymentsColumns,
  approvePaymentColumns
} from 'components/widgets/payments/payments.columns';
import getSuccessfulDetails, {
  getFailedDetails,
  getDuplicateMsgDetails
} from './paymentConfirmation.utils';

// Styles
import styles from './paymentsConfirmation.styles';

export class PaymentsConfirmationComponent extends Component {
  // eslint-disable-next-line react/state-in-constructor
  state = {
    action: this.props.location.state.paymentAction,
    actionInitiated: false,
    challengeErrors: false,
    challengeManager: false,
    data: this.props.location.state.payments,
    datesUpdated: false,
    error: null,
    failedPayments: [],
    hasSufficientFunds: true,
    initialLoading: true,
    isUpdatingTransactions: false,
    loading: false,
    modal: null
  };

  componentDidMount() {
    this.fetchData();
  }

  getAccountIds = () => {
    const uniqueIds = new Set();
    (this.state.data || []).forEach((account) => uniqueIds.add(account.fromAccountId));
    return Array.from(uniqueIds);
  };

  fetchData = () => {
    const fxRates$ = getFxRates();
    const services = [fxRates$];
    const isApprove = this.state.action === ACTIONS.APPROVE;

    if (isApprove) {
      const wireAccounts$ = getWireAccounts({
        includeTemplateOnlyAccounts: true,
        accountIds: this.getAccountIds()
      });
      const wirePreferences$ = getWirePreferences();

      services.push(fxRates$, wirePreferences$, wireAccounts$);
    }

    const combined$ = zip(...services);
    combined$.subscribe((result) => {
      this.setState((state) => ({
        ...state,
        fxRates: result[0],
        initialLoading: false,
        ...(isApprove && {
          wireAccounts: result[0],
          wirePreferences: result[2]
        })
      }));

      // only check balances if wire preferences call for it
      if (
        this.state.action === ACTIONS.APPROVE &&
        (result[2].balanceCheckType === 0 || result[2].balanceCheckType === 1)
      ) {
        this.checkBalances();
      }
    });
  };

  checkBalances = () => {
    // TODO: need to figure out how to handle ACH limits or only apply this check for wire
    const errors = [];
    // get wire payments dated for today from all payments to be approved
    // only non stale wire payments need to be balance checked
    const wirePayments = (this.state.data || []).filter(
      (account) =>
        account.paymentCategory === PAYMENT_CATS.WIRE &&
        moment(account.transactionDt).isSame(moment(), 'd') &&
        (account?.status || '').toLowerCase() !== PAYMENT_STATUS.STALE_DATED
    );

    map(wirePayments, (payment) => {
      const { amount, currency, fromAccountId, payeeName } = payment;

      // get originator account
      const fromAccount = find(this.state.wireAccounts, (account) => account.id === fromAccountId);
      if (!fromAccount) {
        return;
      }
      // get exchange rate
      const rate = find(this.state.fxRates, (rate) => rate.currency === currency);

      // get total payment amounts drawn on same account
      const totalAmounts = chain(wirePayments)
        .filter((payment) => payment.fromAccountId === fromAccountId)
        .reduce(
          (totalAmount, currentPayment) =>
            currentPayment.amount * (rate?.exchangeRate || 1) + totalAmount,
          0
        )
        .value();

      this.setState({
        hasSufficientFunds: totalAmounts.toFixed(2) <= fromAccount.balance
      });
      if (totalAmounts.toFixed(2) > fromAccount.balance) {
        errors.push({
          amount,
          payeeName,
          reason: `${
            this.state.wirePreferences.balanceCheckType === 0
              ? this.props.locale['common.error']
              : this.props.locale['common.warning']
          }: ${this.props.locale['wire.insufficientFunds']} - ${fromAccount.number} - ${
            fromAccount.balance
          }`
        });
      }
    });

    this.setState({
      wireInsufficientFundsErrors: errors.length ? errors : null
    });
  };

  removeAction = (attributes) => {
    const data = this.state.data;
    const filter = data.filter((single) =>
      single.paymentCategory === PAYMENT_CATS.ACH
        ? single.custTrace !== attributes.custTrace
        : single.bankTrace !== attributes.bankTrace
    );
    if (filter.length === 0) {
      this.props.history.push({
        pathname: this.props?.history?.location?.state?.workflowStartPath || WORKSPACES.PYMTCNTR
      });
    }
    this.setState({ data: filter }, this.checkBalances);
  };

  addColumn = () => {
    const remove = [
      {
        cellRenderer: 'CustomCell',
        cellRendererParams: {
          classes: this.props.classes,
          fieldName: 'Remove',
          onRowClick: this.removeAction
        },
        cellView: CustomCell,
        field: 'possibleActions',
        filterable: false,
        headerName: this.props.locale['common.actions'],
        resizable: false,
        sortable: false
      }
    ];
    return remove;
  };

  getColDefs = ({ hasACH, hasWire, isApprove, singleAchReversal }) => {
    if (singleAchReversal) return paymentConfirmationColumnsAchReversal;
    if (isApprove) return approvePaymentColumns;
    if (hasACH && hasWire) return paymentConfirmationColumnsMixed;
    if (hasACH) return paymentConfirmationColumnsAch;
    return paymentConfirmationColumns;
  };

  isApprove = (this.state?.action || '').toLowerCase() === ACTIONS.APPROVE.toLowerCase();

  getColumns = (hasACH, hasWire, singleAchReversal, fxRates) => {
    const isApprove = this.isApprove;
    const columnsDefs = this.getColDefs({
      hasACH,
      hasWire,
      isApprove,
      singleAchReversal
    });

    const columns = getPaymentConfirmationColumns(
      paymentsColumns({
        data: this.state.data,
        isApproveConfirmation: isApprove,
        locale: this.props.locale,
        fxRates
      }),
      columnsDefs,
      isApprove
    );
    return [...(!singleAchReversal ? [...this.addColumn()] : []), ...columns];
  };

  getReversalModalText = (resp) => {
    const { locale } = this.props;
    const { modifyDatetime, modifyUser, updatedTransactionDate } = resp[0];
    let message = locale['payments.confirmation.reversal.dateHasBeenChanged.single'];
    if (this.state.data.length > 1 && resp.length === 1) {
      message = locale['payments.confirmation.reversal.dateHasBeenChanged.singleInBulk'];
    }
    if (resp.length > 1) {
      message = locale['payments.confirmation.reversal.dateHasBeenChanged.multiple'];
    }

    return template(message)({
      actionType: this.state.action,
      ids: resp.map((el) => el.custTraceNum).join(', '),
      modifyDatetime: moment(modifyDatetime).format(DATE_TIME_FORMAT),
      modifyUser,
      updatedTransactionDate: moment(updatedTransactionDate).format(DATE_FORMAT)
    });
  };

  handleProceed = () => {
    this.setState({ modal: null });
    this.handleConfirm();
  };

  validate = () => {
    const { action } = this.state;
    const { locale } = this.props;
    this.setState({ actionInitiated: true, loading: true });
    const achReversals = this.state.data.reduce(
      (acc, el) => [
        ...acc,
        ...(el.achReversal
          ? [
              {
                custTraceNum: el.custTrace,
                transactionDate: moment(el.effectiveEntryDate)
                  .startOf(TIME_FRAME.DAY)
                  .format(API_JAVA_DATE_TIME_FORMAT),
                updatedTransactionDate: moment(el.effectiveEntryDate)
                  .startOf(TIME_FRAME.DAY)
                  .format(API_JAVA_DATE_TIME_FORMAT)
              }
            ]
          : [])
      ],
      []
    );

    if (
      achReversals.length === 0 ||
      ![PAYMENT_ACTIONS.APPROVE, PAYMENT_ACTIONS.REJECT].includes(action.toLowerCase())
    ) {
      return this.handleConfirm();
    }

    return reverseValidateAction$(achReversals).subscribe(({ data, success }) => {
      if (!success) {
        // Check if it's specific error with data
        if (data?.[0]?.modifyUser) {
          return this.setState((state) => ({
            ...state,
            loading: false,
            modal: {
              header:
                action.toLowerCase() === PAYMENT_ACTIONS.APPROVE
                  ? locale['achReversal.approveReversal']
                  : locale['achReversal.rejectReversal'],
              title: this.getReversalModalText(data)
            }
          }));
        }
        return this.setState((state) => ({ ...state, error: getError(data), loading: false }));
      }
      return this.handleConfirm();
    });
  };

  handleConfirm = () => {
    this.setState({ loading: true });
    const payments = this.state.data;
    const { action, fxRates } = this.state;
    const { history, locale, setPaymentActionMessages } = this.props;

    const actionValues = {
      approve: locale['common.approved'],
      delete: locale['common.deleted'],
      reject: locale['common.rejected']
    };

    // ACH payments use custTrace while wire payments use bankTrace
    // After formatting payload filter out any payments the user removed from confirmation grid
    const payload = payments.map((payment) => ({
      action,
      effectiveDate: payment.newTransactionDt || moment(payment.effectiveEntryDate),
      paymentCategory: payment.paymentCategory,
      ...(payment.custTrace && { custTrace: payment.custTrace }),
      ...(payment.bankTrace && { bankTrace: payment.bankTrace }),
      corpPayment: true
    }));

    // Send request to API and handle successful and failed responses
    actionPayments({ paymentActionRequest: payload }).subscribe(({ data, success }) => {
      if (!success) {
        // If not successful then stay on this page and show error
        if (data) {
          if (data.challeangeable === true || data[0].code === API_ERROR.ACCOUNT_LOCKED) {
            return this.setState((state) => ({
              ...state,
              challengeErrors: Boolean(data.numberOfFailedAttempts),
              challengeManager: data,
              loading: false
            }));
          }
        }
        return this.setState((state) => ({
          ...state,
          error: getError(locale, data),
          loading: false
        }));
      }
      // Format object of objects to an array
      const paymentsResponse = Object.values(data);
      // Format success and failure payments based on ACH / Wire / and Action
      const successfulPayments = paymentsResponse
        .filter((response) => response.success === true)
        .map(({ payment }) => getSuccessfulDetails({ action, locale, payment, fxRates }));
      const duplicatePayments = paymentsResponse.reduce(
        (acc, { payment }) => {
          const duplicateDetails = getDuplicateMsgDetails({ action, locale, payment });
          if (duplicateDetails) {
            return {
              duplicateDetails: [
                ...acc.duplicateDetails,
                ...(duplicateDetails.duplicateDetails || [])
              ],
              duplicateMsg: acc.duplicateMsg || duplicateDetails.duplicateMsg,
              hasDuplicatePayment: duplicateDetails.hasDuplicatePayment
            };
          }
          return acc;
        },
        { duplicateDetails: [] }
      );
      const failedPayments = paymentsResponse
        .filter((response) => response.success === false)
        .map(({ payment, reason }) =>
          getFailedDetails({
            action,
            locale,
            payment,
            reason,
            fxRates
          })
        );

      this.setState({ loading: false });
      // Check for successful and failed payments before creating alerts for them
      setPaymentActionMessages([
        ...(successfulPayments.length > 0 // && duplicateWarningMsg < 1
          ? [
              {
                id: 1,
                // Set message for achReversals or for other payment types
                ...(this.singleAchReversal
                  ? {
                      title: locale[`payments.success.reversals.${action.toLowerCase()}`]
                    }
                  : {
                      action,
                      details: successfulPayments,
                      records: paymentsResponse.filter((response) => response.success === true),
                      title: `${successfulPayments.length} ${
                        successfulPayments.length > 1
                          ? locale['common.payments']
                          : locale['common.payment']
                      }
                ${action && actionValues[action.toLowerCase()]}`
                    }),
                ...duplicatePayments,
                success: !duplicatePayments.duplicateMsg
              }
            ]
          : []),
        ...(failedPayments.length > 0
          ? [
              {
                details: failedPayments,
                id: 2,
                records: paymentsResponse.filter((response) => response.success === false),
                success: false,
                title: `${failedPayments.length} ${
                  failedPayments.length > 1 ? locale['common.payments'] : locale['common.payment']
                } ${locale['common.failedTo']} ${
                  action && `${locale[`common.${action.toLowerCase()}`]}`
                }`
              }
            ]
          : [])
      ]);
      // After each call remove opt passcode if any
      jsCookie.remove(HEADERS.X_CM_PASSCODE);
      jsCookie.remove(HEADERS.X_NEXTCM_PASSCODE);
      return history.push({
        pathname: this.props?.history?.location?.state?.workflowStartPath || WORKSPACES.PYMTCNTR
      });
    });
  };

  createPayload = ({ payments, type }) => {
    return payments.reduce((curr, next) => {
      if ((next?.status || '').toLowerCase() === type) {
        return [
          ...curr,
          {
            paymentCategory: next.paymentCategory,
            ...(Boolean(next.custTrace) && { custTrace: next.custTrace }),
            ...(Boolean(next.bankTrace) && { bankTrace: next.bankTrace })
          }
        ];
      }
      return curr;
    }, []);
  };

  getFundWarnings = (data = []) =>
    data.reduce((curr, next) => {
      if (next.warning) {
        return [
          ...curr,
          {
            amount: next?.amount,
            payeeName: next?.payeeName,
            reason: next?.warningResponse
          }
        ];
      }
      return curr;
    }, []);

  updateStaleDatedPayments = () => {
    this.setState({ isUpdatingTransactions: true });
    const payments = this.state.data;
    const { locale } = this.props;

    // Send request to API and handle successful and failed responses
    updateStaleDatedPayments$({
      paymentBalanceCheckRequest: this.createPayload({
        payments,
        type: PAYMENT_STATUS.REQUIRES_MY_APPROVAL
      }),
      staleDatedPaymentDateUpdateRequest: this.createPayload({
        payments,
        type: PAYMENT_STATUS.STALE_DATED
      })
    }).subscribe(({ data, success }) => {
      if (!success) {
        // If not successful then stay on this page and show error
        const invokeChallengeManager =
          data && (data.challeangeable || data[0]?.code === API_ERROR.ACCOUNT_LOCKED);
        if (invokeChallengeManager) {
          this.setState((state) => ({
            ...state,
            challengeErrors: Boolean(data.numberOfFailedAttempts),
            challengeManager: { ...data, callback: this.updateStaleDatedPayments },
            isUpdatingTransactions: false
          }));
          return;
        }

        this.setState((state) => ({
          ...state,
          error: getError(locale, data),
          isUpdatingTransactions: false,
          wireInsufficientFundsErrors: this.getFundWarnings(Object.values(data))
        }));
        return;
      }
      const paymentsResponse = Object.values(data);

      const parsedResults = this.state.data.reduce(
        (curr, next) => {
          const filter =
            next.paymentCategory === PAYMENT_CATS.ACH
              ? { custTrace: next.custTrace }
              : { bankTrace: next.bankTrace };
          const updatedPayment = find(paymentsResponse, filter);
          if (updatedPayment) {
            if (!updatedPayment.success) {
              return {
                data: [...curr.data, { ...next, ...updatedPayment }],
                failedPayments: [...curr.failedPayments, updatedPayment]
              };
            }
            return {
              ...curr,
              data: [
                ...curr.data,
                {
                  ...next,
                  ...updatedPayment,
                  newTransactionDt: updatedPayment.currentTxnDt,
                  transactionDt: updatedPayment.previousTxnDt
                }
              ]
            };
          }
          return { ...curr, data: [...curr.data, next] };
        },
        { data: [], failedPayments: [] }
      );

      this.setState({
        data: parsedResults.data,
        datesUpdated: true,
        failedPayments: parsedResults.failedPayments,
        isUpdatingTransactions: false,
        wireInsufficientFundsErrors: this.getFundWarnings(paymentsResponse)
      });
      // After each call remove opt passcode if any
      jsCookie.remove(HEADERS.X_CM_PASSCODE);
      jsCookie.remove(HEADERS.X_NEXTCM_PASSCODE);
    });
  };

  hasACH =
    Array.isArray(this.state.data) &&
    this.state.data.some((el) => el.paymentCategory === PAYMENT_CATS.ACH);

  hasWire =
    Array.isArray(this.state.data) &&
    this.state.data.some((el) => el.paymentCategory === PAYMENT_CATS.WIRE);

  singleAchReversal =
    Array.isArray(this.state.data) &&
    this.state.data.length === 1 &&
    this.state.data.every((el) => el.achReversal);

  getTitle = () => {
    const {
      locale,
      location: { state }
    } = this.props;
    const workflowAction = state?.paymentAction?.toLowerCase();
    if (this.singleAchReversal) {
      return locale[`payments.confirmation.title.reversals.${workflowAction}`];
    }
    return locale[`payments.confirmation.title.${workflowAction}`];
  };

  render() {
    const {
      classes: { alert, animatingButton, approve, description, list, workflow, wrapper },
      locale,
      location: { state }
    } = this.props;

    const {
      actionInitiated,
      challengeErrors,
      challengeManager,
      data,
      datesUpdated,
      error,
      failedPayments,
      hasSufficientFunds,
      initialLoading,
      isUpdatingTransactions,
      loading,
      modal,
      wireInsufficientFundsErrors,
      wirePreferences
    } = this.state;

    const columns = this.getColumns(
      this.hasACH,
      this.hasWire,
      this.singleAchReversal,
      this.state.fxRates
    );

    const hasFailedPayments = Boolean(failedPayments.length);

    const hasStalePayments = data.some(
      (payment) => (payment.status || '').toLowerCase() === PAYMENT_STATUS.STALE_DATED
    );
    // NOTE: this alert should only be shown when we are in approve flow and data has some same day payments which were updated
    // from stale dates
    const showSameDayAlert =
      this.isApprove && data.some((payment) => payment.sameDay && payment.newTransactionDt);
    const columnDefs =
      hasStalePayments || datesUpdated
        ? columns
        : columns.filter((column) => column.field !== PAYMENT_COLS.NEW_TRANSACTION_DATE);

    const workflowAction = (state.paymentAction || '').toLowerCase();
    if (initialLoading) return <Spinner />;

    return (
      <Workflow title={this.getTitle()}>
        <div className={workflow}>
          {modal && (
            <PaymentsConfirmationModal
              header={modal.header}
              onCancel={() => this.setState({ modal: null })}
              onProceed={this.handleProceed}
              title={modal.title}
            />
          )}
          {challengeManager && (
            <ChallengeManager
              challengeErrorMessage={challengeErrors}
              data={challengeManager}
              onCancel={() =>
                this.setState((state) => ({
                  ...state,
                  challengeManager: false,
                  actionInitiated: false
                }))
              }
              onHide={() =>
                this.setState((state) => ({
                  ...state,
                  challengeManager: false,
                  actionInitiated: false
                }))
              }
              onSubmit={challengeManager.callback || this.handleConfirm}
              open
            />
          )}
          {this.isApprove && hasStalePayments && (
            <Alert className={alert} dismissible={false} variant={ALERTVARIATIONS.INFO}>
              <div className="row">
                <p>{locale['payments.confirmation.approve.updateTransactionMessage']}</p>
                <Button
                  className={`btn btn-primary ${animatingButton} ${
                    isUpdatingTransactions && 'animating'
                  }`}
                  data-qa="update-transactions"
                  onClick={this.updateStaleDatedPayments}
                  variant={PRIMARY}
                >
                  {isUpdatingTransactions
                    ? `${locale['common.updating']}...`
                    : locale['payments.confirmation.approve.updateTransactionButton']}
                </Button>
              </div>
            </Alert>
          )}
          {hasFailedPayments && (
            <PaymentsConfirmationAlert
              errors={failedPayments}
              onHide={() => {
                this.setState({ failedPayments: [] });
              }}
              title={locale['payments.confirmation.approve.updateError']}
            />
          )}
          <div>
            {/* we must have errors, workflow action must be approve and balanceTypeCheck
            must be set to 0 or 1 in order to show the insufficient funds errors dialog,
            also this should not be displayed if update api call has returned any failed payments to
            avoid duplicate error messages */}
            {!hasFailedPayments &&
              wireInsufficientFundsErrors &&
              wireInsufficientFundsErrors?.length > 0 &&
              workflowAction.toLowerCase() === ACTIONS.APPROVE.toLowerCase() &&
              [0, 1].includes(wirePreferences?.balanceCheckType) && (
                <PaymentsWireInsufficientFundsAlert
                  balanceCheckType={wirePreferences?.balanceCheckType}
                  errors={wireInsufficientFundsErrors}
                  onHide={() =>
                    this.setState((state) => ({ ...state, wireInsufficientFundsErrors: null }))
                  }
                />
              )}
            <ul className={list}>
              <li>
                <ExclamationTriangleIcon />
              </li>
              <li className={description}>
                {
                  locale[
                    `payments.confirmation.${workflowAction}${
                      this.singleAchReversal ? '.reversals' : ''
                    }.about`
                  ]
                }
              </li>
            </ul>
            <div>
              {error && (
                <Alert
                  className={alert}
                  onHide={() => this.setState((state) => ({ ...state, error: null }))}
                  title={error}
                  variant={ALERTVARIATIONS.WARNING}
                >
                  <div className="row">
                    <p>{error}</p>
                  </div>
                </Alert>
              )}
              <BasicGrid
                key={hasStalePayments}
                columnDefs={columnDefs}
                defaultState={{
                  pageSize: DEFAULT_PER_PAGE
                }}
                disableSessionStorage
                frameworkComponents={{
                  CustomCell
                }}
                gridId="payment-confirmation-grid"
                label={locale['common.paymentsConfirmation']}
                records={data}
              />
            </div>
            {showSameDayAlert && (
              <>
                <ExclamationTriangleIcon fill="red" />{' '}
                {locale['payments.confirmation.approve.sameDayACH']}
              </>
            )}
            <div className={wrapper}>
              <Button
                className={classnames('btn btn-primary', approve)}
                data-qa={workflowAction}
                disabled={
                  actionInitiated ||
                  loading ||
                  isUpdatingTransactions ||
                  (workflowAction.toLowerCase() === ACTIONS.APPROVE.toLowerCase() &&
                    ((!hasSufficientFunds && wirePreferences.balanceCheckType === 0) ||
                      hasStalePayments))
                }
                onClick={this.validate}
                variant={PRIMARY}
              >
                {this.props.locale[`common.${workflowAction}`]}
              </Button>

              <Button
                className={classnames('btn btn-secondary', 'cancel')}
                data-qa="cancel"
                onClick={() =>
                  this.props.history.push({
                    pathname:
                      this.props?.history?.location?.state?.workflowStartPath ||
                      WORKSPACES.PYMTCNTR
                  })
                }
                variant={SECONDARY}
              >
                {this.props.locale['common.cancel']}
              </Button>
            </div>
          </div>
        </div>
      </Workflow>
    );
  }
}
PaymentsConfirmationComponent.propTypes = {
  classes: PropTypes.shape({}),
  history: PropTypes.shape({
    push: PropTypes.func
  }),
  locale: AppPropTypes.locale,
  location: PropTypes.shape({
    state: PropTypes.shape({
      paymentAction: PropTypes.string,
      payments: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  setPaymentActionMessages: PropTypes.func.isRequired
};
PaymentsConfirmationComponent.defaultProps = {
  classes: {},
  history: {
    push: () => {}
  },
  locale: {},
  location: {}
};

const mapDispatchToProps = (dispatch) => ({
  setPaymentActionMessages: (paymentActionMessages) =>
    dispatch(setPaymentActionMessages(paymentActionMessages))
});

const Styled = withStyles(styles)(PaymentsConfirmationComponent);
const CustomCellStyled = withStyles(CustomCellStyles)(Styled);
const Router = withRouter(CustomCellStyled);
const Localized = withLocale(Router);

export default withRouterConnect(null, mapDispatchToProps)(Localized);
